import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Edit2, ChevronDown } from 'lucide-react';
import prolegionlogo1 from "./../assets/prolegionlogo1.png";
import paidstamp from "./../assets/paidstamp.png";
import html2pdf from 'html2pdf.js';
import { useNavigate } from 'react-router-dom';
import { searchUser, DownloadPdf, getInvoiceSequence, updateInvoice } from '../pages/UserManager/CustomPlans/Helper';
import InvoicePdfExport from './InvoicePdfExport';

const CompanySearchDropdown = ({
    companySearchQuery,
    onSearchChange,
    onCompanySelect,
    isPdfMode,
    isSearching,
    showDropdown,
    companySearchResults,
    setShowCompanyDropdown
}) => {
    if (isPdfMode) {
        return companySearchQuery ? <span className="flex-1">{companySearchQuery}</span> : null;
    }

    return (
        <div className="relative flex-1">
            <input
                type="text"
                value={companySearchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="border border-gray-300 rounded px-2 font-semibold py-1 w-full text-sm"
                placeholder="Search company..."
            />
            {isSearching && (
                <div className="absolute right-2 top-2">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}
            {showDropdown && companySearchResults.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded mt-1 max-h-60 overflow-y-auto shadow-lg">
                    {companySearchResults.map((company, index) => (
                        <div
                            key={index}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                            onClick={() => onCompanySelect(company)}
                        >
                            <div className="font-medium font-semibold text-gray-900">{company.company || 'No Company Name'}</div>
                            <div className="text-gray-600 text-xs">{company.first_name} {company.last_name}</div>
                            {company.gst_number && (
                                <div className="text-gray-500 text-xs">GST: {company.gst_number}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {showDropdown && companySearchResults.length === 0 && !isSearching && companySearchQuery.length >= 2 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded mt-1 shadow-lg">
                    <div className="px-3 py-2 text-sm text-gray-500">No companies found</div>
                </div>
            )}
        </div>
    );
};

const InvoiceForm = ({ 
    isOpen, 
    onClose, 
    selectedPlan = null, 
    userId = null,
    editInvoiceData = null,
    isEditMode = false,
    isPageMode = false,
    isPerformaInvoice = false
}) => {
    const [invoiceData, setInvoiceData] = useState(null);
    const [isPdfMode, setIsPdfMode] = useState(false);
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const [companySearchResults, setCompanySearchResults] = useState([]);
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isLoadingInvoiceNumber, setIsLoadingInvoiceNumber] = useState(false);
    const [selectedCompanyData, setSelectedCompanyData] = useState(null);
    const [pdfExportSnapshot, setPdfExportSnapshot] = useState(null);
    const navigate = useNavigate();
    const getTaxRate = (currency) => {
        return currency === 'USD' ? 5 : 18;
    };
    
    const getTaxLabel = (currency) => {
        return currency === 'USD' ? 'Service Tax (5%)' : 'GST (18%)';
    };
    
    const formatCurrency = (amount, currency) => {
        const numAmount = parseFloat(amount) || 0;
        if (currency === 'INR') {
            return numAmount.toLocaleString('en-IN');
        } else {
            return numAmount.toLocaleString('en-US');
        }
    };

    const getCurrencySymbol = (currency) => {
        return currency === 'INR' ? '₹' : '$';
    };

    const buildHtml2PdfOptions = (filename) => ({
        margin: [0, 0, 0, 0],
        filename,
        image: { type: 'jpeg', quality: 0.93 },
        html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            letterRendering: true,
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
    });

    const fetchInvoiceSequence = async () => {
        setIsLoadingInvoiceNumber(true);
        try {
            const response = await getInvoiceSequence();
            if (response && response.data && response.data.success) {
                return response.data.data || `INV-${Math.floor(Math.random() * 10000)}`;
            } else {
                console.warn('Failed to fetch invoice sequence, using fallback');
                return `INV-${Math.floor(Math.random() * 10000)}`;
            }
        } catch (error) {
            console.error('Error fetching invoice sequence:', error);
            return `INV-${Math.floor(Math.random() * 10000)}`;
        } finally {
            setIsLoadingInvoiceNumber(false);
        }
    };
    const createSafeFilename = (companyName, date) => {
        const cleanCompanyName = companyName
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, '_')
            .trim();
        const formattedDate = new Date(date).toISOString().split('T')[0];
        return `${cleanCompanyName}_${formattedDate}`;
    };
    const initializeInvoiceData = async () => {
        if (isEditMode && editInvoiceData) {
            let billingAddress = {};
            if (editInvoiceData.billingAddress) {
                if (typeof editInvoiceData.billingAddress === 'string') {
                    try {
                        billingAddress = JSON.parse(editInvoiceData.billingAddress);
                    } catch (e) {
                        console.warn('Failed to parse billing address:', e);
                        billingAddress = {};
                    }
                } else {
                    billingAddress = editInvoiceData.billingAddress;
                }
            }

            const editData = {
                invoiceTitle: 'Tax Invoice',
                invoiceNumber: editInvoiceData.invoiceNumber || `INV-${Math.floor(Math.random() * 10000)}`,
                invoiceDate: editInvoiceData.created_at ? new Date(editInvoiceData.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                invoiceReference:
                    editInvoiceData.reference ||
                    editInvoiceData.invoiceReference ||
                    editInvoiceData.ref ||
                    '',
                fromCompany: 'Prolegion Pvt. Ltd.',
                fromFlat: '3rd floor, Biggbang',
                fromStreet: 'Industrial Area, Sector 75',
                fromCity: 'Sahibzada Ajit Singh Nagar',
                fromDistrict: 'Mohali',
                fromState: 'Punjab',
                fromPincode: '160055',
                fromGSTIN: '03AAOCP3400G1ZT',
                billToName: billingAddress.name || '',
                billToCompany: editInvoiceData.companyName || billingAddress.company || '',
                billToEmail: editInvoiceData.companyEmail || '',
                billToGSTIN: billingAddress.gstNumber || '',
                billToAddress: billingAddress.companyAddress || '',
                billToCity: billingAddress.city || '',
                billToState: billingAddress.state || '',
                billToCountry: 'India',
                billToPincode: billingAddress.pincode || '',
                items: [
                    {
                        id: 1,
                        description: editInvoiceData.description || '',
                        currency: editInvoiceData.currency || 'INR',
                        qty: 1,
                        rate: parseFloat(editInvoiceData.amount) || 0,
                        cgst: 18,
                        amount: parseFloat(editInvoiceData.total) || 0,
                    },
                ],
                notes: 'It was great doing business with you.',
                termsConditions: 'Please make the payment by the due date.',
            };

            setInvoiceData(editData);
            setCompanySearchQuery(editData.billToCompany);
            setSelectedCompanyData({
                email: editInvoiceData.companyEmail || '',
                user_id: editInvoiceData.userId || '',
                id: editInvoiceData.userId || '',
                company: editData.billToCompany,
                first_name: editData.billToName.split(' ')[0] || '',
                last_name: editData.billToName.split(' ').slice(1).join(' ') || '',
            });
        } else {
            const defaultData = {
                invoiceTitle: 'Tax Invoice',
                invoiceNumber: '',
                invoiceDate: new Date().toISOString().split('T')[0],
                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                fromCompany: 'Prolegion Pvt. Ltd.',
                fromFlat: '3rd floor, Biggbang',
                fromStreet: 'Industrial Area, Sector 75',
                fromCity: 'Sahibzada Ajit Singh Nagar',
                fromDistrict: 'Mohali',
                fromState: 'Punjab',
                fromPincode: '160055',
                fromGSTIN: '03AAOCP3400G1ZT',
                billToName: '',
                billToCompany: '',
                billToEmail: '',
                billToGSTIN: '',
                billToAddress: '',
                billToCity: '',
                billToState: '',
                billToCountry: '',
                billToPincode: '',
                items: [
                    {
                        id: 1,
                        description: '',
                        currency: 'INR',
                        qty: 1,
                        rate: 0,
                        cgst: 18,
                        amount: 0,
                    },
                ],
                notes: 'It was great doing business with you.',
                termsConditions: 'Please make the payment by the due date.',
            };
            setInvoiceData(defaultData);
        }
    };

    useEffect(() => {
        if (isOpen) {
            initializeInvoiceData();
        }
    }, [isOpen, selectedPlan, isEditMode, editInvoiceData]);

    useEffect(() => {
        if (isOpen && invoiceData && !isEditMode) {
            setCompanySearchQuery(invoiceData.billToCompany);
        }
    }, [isOpen, invoiceData?.billToCompany, isEditMode]);

    const handleCompanySearch = async (query) => {
        setCompanySearchQuery(query);
        setInvoiceData(prev => ({
            ...prev,
            billToCompany: query
        }));

        if (query.length < 2) {
            setCompanySearchResults([]);
            setShowCompanyDropdown(false);
            return;
        }

        setIsSearching(true);
        try {
            const response = await searchUser(query);
            if (response.data && response.data.success && response.data.result.length > 0) {
                setCompanySearchResults(response.data.result);
                setShowCompanyDropdown(true);
            } else {
                setCompanySearchResults([]);
                setShowCompanyDropdown(false);
            }
        } catch (error) {
            console.error('Error searching companies:', error);
            setCompanySearchResults([]);
            setShowCompanyDropdown(false);
        } finally {
            setIsSearching(false);
        }
    };

    const handleCompanySelect = (selectedCompany) => {
        setSelectedCompanyData(selectedCompany);
        setInvoiceData(prev => ({
            ...prev,
            billToName: `${selectedCompany.first_name} ${selectedCompany.last_name}`,
            billToCompany: selectedCompany.company || '',
            billToEmail: selectedCompany.email || '',
            billToGSTIN: selectedCompany.gst_number || '',
            billToAddress: selectedCompany.billing_address?.address || '',
            billToCity: selectedCompany.billing_address?.city || '',
            billToState: selectedCompany.billing_address?.state || '',
            billToCountry: selectedCompany.billing_address?.country || '',
            billToPincode: selectedCompany.billing_address?.pincode || '',
        }));
        setCompanySearchQuery(selectedCompany.company || '');
        setShowCompanyDropdown(false);
    };

    const validateInvoice = () => {
        const hasValidItem = invoiceData.items.some(item => 
            item.description.trim() !== '' && parseFloat(item.rate) > 0
        );

        if (!hasValidItem) {
            setErrorMessage('Please fill in at least one item description and amount before saving.');
            return false;
        }

        setErrorMessage('');
        return true;
    };

    const convertPdfToBuffer = async (invoiceNumberForFile) => {
        const el = document.getElementById('invoice-pdf-export');
        if (!el) {
            throw new Error('PDF export template not found');
        }
        const num = invoiceNumberForFile ?? invoiceData.invoiceNumber;
        const opt = buildHtml2PdfOptions(`${num}.pdf`);
        return html2pdf().set(opt).from(el).outputPdf('blob');
    };

    const callUpdateInvoiceApi = async (pdfBuffer, finalInvoiceNumber) => {
        try {
            const subtotal = calculateSubTotal();
            const gst = calculateCGST();
            const total = calculateTotal();
            const primaryCurrency = invoiceData.items[0]?.currency || 'INR';
            const description = invoiceData.items[0]?.description || 'Service description';
            const invoiceTypeValue = 'taxbased';

            const billingAddress = {
                company: invoiceData.billToCompany || '',
                name: invoiceData.billToName || '',
                companyAddress: invoiceData.billToAddress || '',
                city: invoiceData.billToCity || '',
                state: invoiceData.billToState || '',
                gstNumber: invoiceData.billToGSTIN || ''
            };

            const formData = new FormData();
            formData.append('file', pdfBuffer, `${finalInvoiceNumber || invoiceData.invoiceNumber}.pdf`);
            formData.append('companyName', invoiceData.billToCompany || '');
            formData.append('invoiceType', invoiceTypeValue);
            formData.append('invoiceId', editInvoiceData?.id || editInvoiceData?.invoiceId || '');
            formData.append('invoiceNumber', finalInvoiceNumber || invoiceData.invoiceNumber);

            for (let [key, value] of formData.entries()) {
                if (key === 'file') {
                    console.log(`${key}: PDF File (${value.size} bytes) - ${value.name}`);
                } else {
                    console.log(`${key}: "${value}"`);
                }
            }

            const response = await updateInvoice(formData);
            
            if (response && response.data.success) {
                setTimeout(() => {
                    onClose();
                }, 1000);
            } else {
                setErrorMessage('Failed to update invoice. Please try again.');
                setTimeout(() => {
                    onClose();
                }, 1000);
            }
        } catch (error) {
            setErrorMessage('Error updating invoice. Please try again.');
            setTimeout(() => {
                onClose();
            }, 1000);
        }
    };

    const callDownloadPdfApi = async (pdfBuffer, finalInvoiceNumber) => {
        try {
            const subtotal = calculateSubTotal();
            const gst = calculateCGST();
            const total = calculateTotal();
            const primaryCurrency = invoiceData.items[0]?.currency || 'INR';
            const description = invoiceData.items[0]?.description || 'Service description';
            const invoiceTypeValue = 'taxbased';

            const billingAddress = {
                company: invoiceData.billToCompany || '',
                name: invoiceData.billToName || '',
                companyAddress: invoiceData.billToAddress || '',
                city: invoiceData.billToCity || '',
                state: invoiceData.billToState || '',
                gstNumber: invoiceData.billToGSTIN || ''
            };

            const formData = new FormData();
            formData.append('file', pdfBuffer, `${finalInvoiceNumber || invoiceData.invoiceNumber}.pdf`);
            formData.append('companyName', invoiceData.billToCompany || '');
            formData.append('companyEmail', selectedCompanyData?.email || '');
            formData.append('invoiceNumber', finalInvoiceNumber || invoiceData.invoiceNumber);
            formData.append('amount', subtotal.toString());
            formData.append('gst', gst.toString());
            formData.append('total', total.toString());
            formData.append('userId', selectedCompanyData?.user_id || selectedCompanyData?.id || '');
            formData.append('billingAddress', JSON.stringify(billingAddress));
            formData.append('currency', primaryCurrency);
            formData.append('description', description);
            formData.append('invoiceType', invoiceTypeValue);
            for (let [key, value] of formData.entries()) {
                if (key === 'file') {
                    console.log(`${key}: PDF File (${value.size} bytes) - ${value.name}`);
                } else {
                    console.log(`${key}: "${value}"`);
                }
            }

            const response = await DownloadPdf(formData);
            if (response && response.data.success) {
                console.log('Invoice created successfully:');
                if (!isPerformaInvoice) {
                    navigate('/PaymentsAndInvoices/ExternalPayments');
                }
                setTimeout(() => {
                    onClose();
                }, 1000);
            } else {
                setErrorMessage('Failed to create invoice. Please try again.');
                setTimeout(() => {
                    onClose();
                }, 1000);
            }
        } catch (error) {
            setErrorMessage('Error creating invoice. Please try again.');
            setTimeout(() => {
                onClose();
            }, 1000);
        }
    };
    const createProformaFilename = (companyName, date) => {
        const cleanCompanyName = companyName
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, '_')
            .trim();
        const formattedDate = new Date(date).toISOString().split('T')[0];
        return `${cleanCompanyName}_${formattedDate}.pdf`;
    };

    const downloadInvoicePDF = async () => {
        if (!validateInvoice()) {
            return;
        }
        setIsDownloading(true);
        try {
            let finalInvoiceNumber = invoiceData.invoiceNumber;
            let snapshot = { ...invoiceData };

            if (!isEditMode && !isPerformaInvoice) {
                finalInvoiceNumber = await fetchInvoiceSequence();
                snapshot = { ...snapshot, invoiceNumber: finalInvoiceNumber };
                setInvoiceData(snapshot);
                await new Promise((resolve) => setTimeout(resolve, 100));
            } else if (isPerformaInvoice) {
                finalInvoiceNumber = 'PROFORMA';
                snapshot = { ...snapshot, invoiceNumber: 'PROFORMA' };
                setInvoiceData(snapshot);
                await new Promise((resolve) => setTimeout(resolve, 100));
            } else {
                snapshot = { ...snapshot, invoiceNumber: finalInvoiceNumber };
            }

            setPdfExportSnapshot(snapshot);
            await new Promise((resolve) => setTimeout(resolve, 320));

            const pdfEl = document.getElementById('invoice-pdf-export');
            if (!pdfEl) {
                throw new Error('PDF export template not found');
            }

            const filename = isPerformaInvoice
                ? createProformaFilename(
                      snapshot.billToCompany || 'ProformaInvoice',
                      snapshot.invoiceDate
                  )
                : `${finalInvoiceNumber}.pdf`;
            const opt = buildHtml2PdfOptions(filename);

            if (isPerformaInvoice) {
                await html2pdf().set(opt).from(pdfEl).save();
                setTimeout(() => {
                    onClose();
                }, 1000);
            } else {
                await html2pdf().set(opt).from(pdfEl).save();
                const pdfBuffer = await convertPdfToBuffer(finalInvoiceNumber);
                if (isEditMode) {
                    await callUpdateInvoiceApi(pdfBuffer, finalInvoiceNumber);
                } else {
                    await callDownloadPdfApi(pdfBuffer, finalInvoiceNumber);
                }
            }
        } catch (error) {
            console.error('Error in downloadInvoicePDF:', error);
            setErrorMessage(`Failed to ${isEditMode ? 'update' : 'process'} invoice. Please try again.`);
        } finally {
            setPdfExportSnapshot(null);
            setIsDownloading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setInvoiceData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...invoiceData.items];
        newItems[index][field] = value;
        if (field === 'rate' || field === 'currency') {
            const qty = parseFloat(newItems[index].qty) || 0;
            const rate = parseFloat(newItems[index].rate) || 0;
            const taxRate = getTaxRate(newItems[index].currency);
            
            newItems[index].cgst = taxRate;
            
            const subtotal = qty * rate;
            const taxAmount = subtotal * (taxRate / 100);
            newItems[index].amount = subtotal + taxAmount;
        }

        setInvoiceData(prev => ({
            ...prev,
            items: newItems
        }));
        if (errorMessage && (field === 'description' || field === 'rate')) {
            setErrorMessage('');
        }
    };

    const addItem = () => {
        const newItem = {
            id: Date.now(),
            description: '',
            currency: 'INR',
            qty: 1,
            rate: 0,
            cgst: 18,
            amount: 0
        };
        setInvoiceData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
    };

    const removeItem = (index) => {
        setInvoiceData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const calculateSubTotal = () => {
        return invoiceData.items.reduce((sum, item) => {
            const qty = parseFloat(item.qty) || 0;
            const rate = parseFloat(item.rate) || 0;
            return sum + (qty * rate);
        }, 0);
    };

    const calculateCGST = () => {
        return invoiceData.items.reduce((sum, item) => {
            const qty = parseFloat(item.qty) || 0;
            const rate = parseFloat(item.rate) || 0;
            const cgst = parseFloat(item.cgst) || 0;
            const subtotal = qty * rate;
            return sum + (subtotal * cgst / 100);
        }, 0);
    };

    const calculateTotal = () => {
        return calculateSubTotal() + calculateCGST();
    };

    const EditableField = ({ value, onSave, multiline = false, className = "", placeholder = "", style = {} }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [tempValue, setTempValue] = useState(value);
        const handleSave = () => {
            onSave(tempValue);
            setIsEditing(false);
        };
        
        if (isPdfMode) {
            return value ? <div className={className} style={style}>{value}</div> : null;
        }
        
        const displayValue = value || placeholder;
        
        if (isEditing) {
            return multiline ? (
                <textarea
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSave();
                        }
                    }}
                    className={`border border-blue-300 rounded px-2 py-1 w-full ${className}`}
                    placeholder={placeholder}
                    autoFocus
                />
            ) : (
                <input
                    type="text"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleSave();
                        }
                    }}
                    className={`border border-blue-300 rounded px-2 py-1 w-full ${className}`}
                    placeholder={placeholder}
                    autoFocus
                />
            );
        }
        return (
            <div
                className={`cursor-pointer hover:bg-gray-200 rounded group relative ${className} ${!value ? 'text-gray-400 italic' : ''}`}
                onClick={() => setIsEditing(true)}
            >
                {displayValue}
                <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 pl-3 absolute top-1 right-1 no-print" />
            </div>
        );
    };

    if (!isOpen || !invoiceData) {
        return null;
    }

    const containerClass = isPageMode 
        ? "bg-white rounded-lg w-full print:max-h-none print:overflow-visible"
        : "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4";

    const contentClass = isPageMode
        ? "bg-white rounded-lg w-full print:max-h-none print:overflow-visible"
        : "bg-white rounded-lg w-full max-w-4xl print:max-h-none print:overflow-visible max-h-[90vh] overflow-y-auto";

    return (
        <div className={containerClass}>
            <div className={contentClass}>
                {pdfExportSnapshot && (
                    <div
                        style={{
                            position: 'fixed',
                            left: '-20000px',
                            top: 0,
                            width: '8.5in',
                            minHeight: '11in',
                            zIndex: -1,
                            pointerEvents: 'none',
                            overflow: 'visible',
                        }}
                        aria-hidden
                    >
                        <InvoicePdfExport
                            data={pdfExportSnapshot}
                            isPerformaInvoice={isPerformaInvoice}
                        />
                    </div>
                )}
                {!isPageMode && (
                    <div className="flex justify-between items-center p-4 border-b">
                        <h2 className="text-xl font-bold text-gray-800">
                            {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 no-print"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                )}

                <div className="p-4 bg-white" id="invoice-content" style={{
                    fontFamily: "'Segoe UI', sans-serif",
                    backgroundColor: '#f8f8f8'
                }}>
                    <div className="bg-white p-8 rounded-lg shadow-lg" style={{
                        boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div className="flex flex-row items-start gap-16 mb-3">
                            <img src={prolegionlogo1} className="h-12 w-auto mb-2" alt="prolegion" />
                            <div className="text-2xl font-bold ml-auto mr-20 mt-1 text-[#3985B6]" style={{ color: '#237ed9' }}>
                                {isPerformaInvoice ? 'Proforma Invoice' : 'Tax Invoice'}
                            </div>
                        </div>

                        <div className="flex justify-start mb-2">
                            <div className="w-1/2 text-sm leading-tight">
                                <EditableField
                                    value={invoiceData.fromCompany}
                                    onSave={(val) => handleInputChange('fromCompany', val)}
                                    className="font-bold text-sm mb-1"
                                />
                                <div className="space-y-0.5">
                                    {[
                                        ['Building No./Flat No.', 'fromFlat'],
                                        ['Road/street', 'fromStreet'],
                                        ['City/Town/village', 'fromCity'],
                                        ['District', 'fromDistrict'],
                                        ['State', 'fromState'],
                                        ['Pincode', 'fromPincode'],
                                        ['GST', 'fromGSTIN'],
                                    ].map(([label, fieldKey]) => (
                                        <div key={fieldKey} className="my-0.5 p-0">
                                            <span className="text-gray-700">{label}: </span>
                                            <EditableField
                                                value={invoiceData[fieldKey]}
                                                onSave={(val) => handleInputChange(fieldKey, val)}
                                                className="inline"
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-5 w-full">
                                    <p className="font-bold mb-1">To:</p>
                                    <div className="space-y-0.5">
                                        <div className="flex items-center text-sm gap-1">
                                            <CompanySearchDropdown
                                                companySearchQuery={companySearchQuery}
                                                onSearchChange={handleCompanySearch}
                                                onCompanySelect={handleCompanySelect}
                                                isPdfMode={isPdfMode}
                                                isSearching={isSearching}
                                                showDropdown={showCompanyDropdown}
                                                companySearchResults={companySearchResults}
                                                setShowCompanyDropdown={setShowCompanyDropdown}
                                            />
                                        </div>
                                        <EditableField
                                            value={invoiceData.billToName}
                                            onSave={(val) => handleInputChange('billToName', val)}
                                            className="font-bold block"
                                            placeholder="Person of Contact"
                                        />
                                        {(!isPdfMode || invoiceData.billToEmail?.trim()) && (
                                        <div className='flex gap-1'>
                                            <span className="text-gray-700">Email: </span>
                                            <EditableField
                                                value={invoiceData.billToEmail}
                                                onSave={(val) => handleInputChange('billToEmail', val)}
                                                className="inline"
                                                placeholder="Enter email"
                                            />
                                        </div>
                                        )}
                                        {(!isPdfMode || invoiceData.billToAddress?.trim()) && (
                                        <div className={`flex gap-1 ${isPdfMode ? 'items-start' : ''}`}>
                                            <span className="text-gray-700 flex-shrink-0">Billing Address: </span>
                                            <EditableField
                                                value={invoiceData.billToAddress}
                                                onSave={(val) => handleInputChange('billToAddress', val)}
                                                className={`${isPdfMode ? 'inline' : 'block'}`}
                                                style={isPdfMode ? { wordBreak: 'normal', overflowWrap: 'break-word', whiteSpace: 'normal' } : {}}
                                                placeholder="Enter billing address"
                                                multiline={!isPdfMode}
                                            />
                                        </div>
                                        )}
                                        {(!isPdfMode || invoiceData.billToCity?.trim() || invoiceData.billToState?.trim()) && (
                                        <div className='flex gap-1'>
                                            <span className="text-gray-700">City/State: </span>
                                            <EditableField
                                                value={invoiceData.billToCity || invoiceData.billToState ? `${invoiceData.billToCity}${invoiceData.billToState ? ', ' + invoiceData.billToState : ''}` : ''}
                                                onSave={(val) => {
                                                    const [city, state] = val.split(', ');
                                                    handleInputChange('billToCity', city || '');
                                                    handleInputChange('billToState', state || '');
                                                }}
                                                className="block"
                                                placeholder="City, State"
                                            />
                                        </div>
                                        )}
                                        {(!isPdfMode || invoiceData.billToPincode?.trim() || invoiceData.billToCountry?.trim()) && (
                                        <div className='flex gap-1'>
                                            <span className="text-gray-700">PinCode: </span>
                                            <EditableField
                                                value={invoiceData.billToCountry || invoiceData.billToPincode ? `${invoiceData.billToCountry || 'India'}${invoiceData.billToPincode ? ' - ' + invoiceData.billToPincode : ''}` : ''}
                                                onSave={(val) => {
                                                    const [country, pincode] = val.split(' - ');
                                                    handleInputChange('billToCountry', country || '');
                                                    handleInputChange('billToPincode', pincode || '');
                                                }}
                                                className="block"
                                                placeholder="Country - Pincode"
                                            />
                                        </div>
                                        )}
                                        {(invoiceData.billToGSTIN || !isPdfMode) && (
                                        <div className='flex gap-1'>
                                            <span className="text-gray-700">GST: </span>
                                            <EditableField 
                                                value={invoiceData.billToGSTIN} 
                                                onSave={(val) => handleInputChange('billToGSTIN', val)} 
                                                className="inline"
                                                placeholder="GSTIN"
                                            />
                                        </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="w-1/3 text-sm leading-tight ml-auto text-left">
                                <p className="font-bold mb-1">Invoice Details</p>
                                <div className="my-0.5 p-0">
                                    <span>Invoice Date: </span>
                                    {isPdfMode ? (
                                        <span>{invoiceData.invoiceDate}</span>
                                    ) : (
                                        <input
                                            type="date"
                                            value={invoiceData.invoiceDate}
                                            onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                                            className="text-sm border border-gray-300 rounded px-1 py-0.5 ml-1"
                                        />
                                    )}
                                </div>
                                {!isPdfMode ? (
                                    <div className="my-0.5 p-0 flex flex-wrap items-center gap-1">
                                        <span className="text-gray-700">Reference: </span>
                                        <input
                                            type="text"
                                            value={invoiceData.invoiceReference || ''}
                                            onChange={(e) => handleInputChange('invoiceReference', e.target.value)}
                                            placeholder="Optional"
                                            className="text-sm border border-gray-300 rounded px-2 py-0.5 flex-1 min-w-[120px]"
                                        />
                                    </div>
                                ) : (
                                    (invoiceData.invoiceReference || '').trim() !== '' && (
                                        <div className="my-0.5 p-0">
                                            <span>Reference: </span>
                                            <span>{invoiceData.invoiceReference}</span>
                                        </div>
                                    )
                                )}
                                {!isPerformaInvoice && isPdfMode && invoiceData.invoiceNumber && (
                                    <div className="my-0.5 p-0 mt-2">
                                        <span>Invoice Number: </span>
                                        <span className="font-medium text-gray-800 px-2 py-1 rounded text-sm">
                                            {invoiceData.invoiceNumber}
                                        </span>
                                    </div>
                                )}
                                {!isPerformaInvoice && isEditMode && !isPdfMode && (
                                    <div className="my-0.5 p-0 mt-2">
                                        <span>Invoice Number: </span>
                                        <span className="font-medium text-gray-800 px-2 py-1 rounded text-sm">
                                            {invoiceData.invoiceNumber}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-8 mb-5">
                            <table className="w-full border-collapse" style={{ marginTop: '10px' }}>
                                <thead>
                                    <tr>
                                        <th className="px-3 py-3 border-b border-gray-300 text-left bg-gray-100">Item Description</th>
                                        {!isPdfMode && (<th className="px-3 py-3 border-b border-gray-300 text-center bg-gray-100">Currency</th>)}
                                        <th className="px-3 py-3 border-b border-gray-300 text-center bg-gray-100">Amount</th>
                                        <th className="px-3 py-3 border-b border-gray-300 text-center bg-gray-100">
                                            {invoiceData.items.length > 0 ? getTaxLabel(invoiceData.items[0].currency) : 'Tax'}
                                        </th>
                                        <th className="px-3 py-3 border-b border-gray-300 text-center bg-gray-100">Total</th>
                                        {!isPdfMode && <th className="px-3 py-3 border-b border-gray-300 text-center bg-gray-100 no-print">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceData.items.map((item, index) => {
                                        const currencySymbol = getCurrencySymbol(item.currency);
                                        const subtotal = item.qty * item.rate;
                                        const taxRate = getTaxRate(item.currency);
                                        const taxAmount = subtotal * (taxRate / 100);
                                        const total = subtotal + taxAmount;
                                        
                                        return (
                                            <tr key={item.id}>
                                                <td className="px-3 py-3 border-b border-gray-300 align-top">
                                                    {isPdfMode ? (
                                                        <div>
                                                            {item.description ? (
                                                                <div style={{ maxWidth: '300px' }} className="text-sm whitespace-pre-line">{item.description}</div>
                                                            ) : (
                                                                <span className="text-gray-400 italic">Service description</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <textarea
                                                            value={item.description}
                                                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                                            className="border border-gray-300 rounded px-2 py-1 text-sm w-full min-h-10 resize-y"
                                                            placeholder="Enter service description"
                                                            rows={2}
                                                            style={{ resize: 'vertical' }}
                                                        />
                                                    )}
                                                </td>
                                                {!isPdfMode && (<td className="px-3 py-3 border-b border-gray-300 text-center align-top">
                                                    {isPdfMode ? (
                                                        <div className="font-medium">{currencySymbol}</div>
                                                    ) : (
                                                        <select
                                                            value={item.currency}
                                                            onChange={(e) => handleItemChange(index, 'currency', e.target.value)}
                                                            className="border border-gray-300 rounded px-2 py-1 text-sm w-full text-center"
                                                        >
                                                            <option value="INR">₹ INR</option>
                                                            <option value="USD">$ USD</option>
                                                        </select>
                                                    )}
                                                </td>)}
                                                <td className="px-3 py-3 border-b border-gray-300 text-center align-top">
                                                    {isPdfMode ? (
                                                        <div>{currencySymbol} {formatCurrency(item.rate, item.currency)}</div>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={item.rate ? formatCurrency(item.rate, item.currency) : ''}
                                                            onChange={(e) => {
                                                                const cleanValue = e.target.value.replace(/[^\d.]/g, '');
                                                                const numericValue = parseFloat(cleanValue) || 0;
                                                                handleItemChange(index, 'rate', numericValue);
                                                            }}
                                                            className="border border-gray-300 rounded px-2 py-1 text-sm w-full text-center"
                                                            placeholder="0"
                                                        />
                                                    )}
                                                </td>
                                                <td className="px-3 py-3 border-b border-gray-300 text-center align-top">
                                                    {currencySymbol} {formatCurrency(taxAmount, item.currency)}
                                                </td>
                                                <td className="px-3 py-3 border-b border-gray-300 text-center align-top font-medium">
                                                    {currencySymbol} {formatCurrency(total, item.currency)}
                                                </td>
                                                {!isPdfMode && (
                                                    <td className="px-3 py-3 border-b border-gray-300 text-center align-top">
                                                        <button
                                                            onClick={() => removeItem(index)}
                                                            className="text-red-500 hover:text-red-700 p-1"
                                                            disabled={invoiceData.items.length === 1}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {!isPdfMode && (
                                <button onClick={addItem} className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 px-4 py-2 text-sm no-print">
                                    <Plus className="w-4 h-4" /><span>Add Item</span>
                                </button>
                            )}
                        </div>

                        <div className="flex justify-between mb-5 mt-5 gap-6 flex-wrap">
                            <div className="flex flex-col items-start justify-start max-w-lg space-y-3">
                                <div>
                                    <h3 className="font-semibold text-gray-700 text-sm mb-1">Terms and Conditions:</h3>
                                    {isPdfMode ? (
                                        <div className="text-sm w-full text-gray-800 whitespace-pre-line">
                                            {invoiceData.termsConditions}
                                        </div>
                                    ) : (
                                        <textarea
                                            value={invoiceData.termsConditions}
                                            onChange={(e) => {
                                                const words = e.target.value.trim().split(/\s+/);
                                                if (words.length <= 200) {
                                                    handleInputChange('termsConditions', e.target.value);
                                                }
                                            }}
                                            rows={4}
                                            placeholder="Add terms and conditions (max 200 words)..."
                                            className="w-96 border border-gray-300 rounded px-2 py-1 text-sm resize-y"
                                        />
                                    )}
                                    {!isPdfMode && invoiceData.termsConditions.trim().split(/\s+/).length > 200 && (
                                        <div className="text-red-500 text-xs mt-1">
                                            Maximum 200 words allowed.
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="w-56 ml-auto text-right">
                                <div className="space-y-2">
                                    {(() => {
                                        const primaryCurrency = invoiceData.items[0]?.currency || 'INR';
                                        const currencySymbol = getCurrencySymbol(primaryCurrency);

                                        return (
                                            <>
                                                <div className="flex justify-between items-center">
                                                    <span className="font-normal text-gray-700">Subtotal:</span>
                                                    <span className="font-normal text-gray-700">{currencySymbol} {formatCurrency(calculateSubTotal(), primaryCurrency)}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="font-normal text-gray-700">GST (18%):</span>
                                                    <span className="font-normal text-gray-700">{currencySymbol} {formatCurrency(calculateCGST(), primaryCurrency)}</span>
                                                </div>
                                                <div className="flex justify-between items-center font-bold text-black border-t pt-2">
                                                    <span>Total:</span>
                                                    <span>{currencySymbol} {formatCurrency(calculateTotal(), primaryCurrency)}</span>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
                {!isPdfMode && !isPreviewMode && (
                    <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 no-print">
                        {errorMessage && (
                            <div className="flex-1 mr-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                                {errorMessage}
                            </div>
                        )}
                        {!isPageMode && (
                            <button onClick={onClose} className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                        )}
                        <button
                            onClick={() => {
                                if (validateInvoice()) {
                                    setIsPdfMode(true);
                                    setIsPreviewMode(true);
                                }
                            }}
                            className="px-4 py-2 bg-yellow-500 text-white rounded-md"
                        >
                            Preview PDF
                        </button>
                    </div>
                )}
                {isPdfMode && isPreviewMode && (
                    <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 no-print">
                        <button
                            onClick={() => {
                                setIsPdfMode(false);
                                setIsPreviewMode(false);
                            }}
                            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Back to Edit
                        </button>
                        <button
                            onClick={downloadInvoicePDF}
                            disabled={isDownloading}
                            className="px-4 py-2 bg-[#3985B6] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDownloading ? (
                                <div className="flex items-center space-x-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>
                                        {isLoadingInvoiceNumber ? 'Generating Invoice Number...' : 
                                         isPerformaInvoice ? 'Downloading...' : 'Generating Invoice...'}
                                    </span>
                                </div>
                            ) : (
                                isPerformaInvoice ? 'Download Invoice' : 'Generate Invoice'
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoiceForm;